import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Send, Sparkles } from "lucide-react";
import BackButton from "@/components/BackButton";

const SUGGESTIONS = [
  "I need Instagram assets for Gummies.",
  "Show me the Apollo sales catalog for Massachusetts.",
  "Download everything for Blue Dream.",
  "Where is the newest logo?",
  "What changed this month?",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `sess-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function send(text) {
    const q = text ?? input;
    if (!q.trim() || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setSending(true);

    try {
      const token = localStorage.getItem("apollo_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") break;
          // Detect backend-surfaced errors (Emergent LLM budget, upstream API errors, etc.)
          if (!errored && (data.startsWith("[Error]") || /BadRequestError|Budget has been exceeded|litellm/i.test(data))) {
            errored = true;
            const friendly = /budget/i.test(data)
              ? "The Apollo Assistant is temporarily unavailable — the AI service budget has been exceeded. Please contact your Apollo admin to top up the Emergent LLM key."
              : "The Apollo Assistant is temporarily unavailable. Please try again in a moment.";
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: friendly, error: true };
              return c;
            });
            continue;
          }
          if (errored) continue;
          full += data;
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", content: full };
            return c;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Something went wrong. Please try again.", error: true };
        return c;
      });
    }
    setSending(false);
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] slide-up" data-testid="ai-page">
      <BackButton />
      <header className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Concierge</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight flex items-center gap-3">
          Apollo Assistant <Sparkles className="h-6 w-6" style={{ color: "#FBC90C" }} />
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">Find assets, answer brand questions, or assemble a package. Powered by Claude Sonnet 4.5.</p>
      </header>

      <div className="flex-1 overflow-y-auto border border-border rounded-sm bg-surface p-6 space-y-6" data-testid="ai-messages">
        {messages.length === 0 && (
          <div className="max-w-xl">
            <p className="text-sm text-muted-foreground mb-4">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="px-3 py-1.5 text-xs border border-border rounded-sm hover:bg-surface-elev transition" data-testid={`ai-suggestion-${s.slice(0, 8)}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-sm p-4 ${
                m.role === "user"
                  ? "bg-foreground text-background"
                  : m.error
                    ? "border border-[#E15C2C]/50 bg-[#E15C2C]/10 text-[#E15C2C]"
                    : "bg-surface-elev"
              }`}
              data-testid={m.error ? "ai-error-message" : undefined}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-70 mb-1">
                {m.role === "user" ? "You" : m.error ? "Unavailable" : "Apollo"}
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed">
                {m.content || <span className="opacity-50">Thinking…</span>}
              </div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-3 items-center border border-border rounded-sm bg-surface px-4 py-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask Apollo anything…"
          className="flex-1 bg-transparent outline-none py-2 text-sm"
          disabled={sending}
          data-testid="ai-input"
        />
        <button type="submit" disabled={sending || !input.trim()} className="p-2 rounded-sm bg-foreground text-background disabled:opacity-30" data-testid="ai-send">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
